import { useState, useEffect } from "react";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  DrawerFooter,
} from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { AdvancedFilters, PRICE_RANGES, RATING_RANGES } from "@/types/advancedFilters";
import { useFavorites } from "@/contexts/FavoritesContext";

interface AdvancedFilterDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  filters: AdvancedFilters;
  onFiltersChange: (filters: AdvancedFilters) => void;
}

export function AdvancedFilterDrawer({
  open,
  onOpenChange,
  filters,
  onFiltersChange,
}: AdvancedFilterDrawerProps) {
  const [tempFilters, setTempFilters] = useState<AdvancedFilters>(filters);
  const { hasFavorites } = useFavorites();

  useEffect(() => {
    setTempFilters(filters);
  }, [filters]);

  const handlePriceRangeChange = (rangeId: string, checked: boolean) => {
    setTempFilters(prev => ({
      ...prev,
      priceRanges: checked
        ? [...prev.priceRanges, rangeId]
        : prev.priceRanges.filter(id => id !== rangeId)
    }));
  };

  const handleRatingRangeChange = (rangeId: string, checked: boolean) => {
    setTempFilters(prev => ({
      ...prev,
      ratingRanges: checked
        ? [...prev.ratingRanges, rangeId]
        : prev.ratingRanges.filter(id => id !== rangeId)
    }));
  };

  const handleFeaturedChange = (checked: boolean) => {
    setTempFilters(prev => ({
      ...prev,
      featured: checked
    }));
  };

  const handleApply = () => {
    onFiltersChange(tempFilters);
    onOpenChange(false);
  };

  const handleClearAll = () => {
    const clearedFilters = { priceRanges: [], ratingRanges: [], featured: false, myFavorites: false };
    setTempFilters(clearedFilters);
    onFiltersChange(clearedFilters);
    onOpenChange(false);
  };

  const handleCancel = () => {
    setTempFilters(filters);
    onOpenChange(false);
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent>
        <div className="mx-auto flex h-full w-full max-w-sm flex-col">
          <DrawerHeader>
            <DrawerTitle>Advanced Filters</DrawerTitle>
            <DrawerDescription>
              Refine your search with additional filters
            </DrawerDescription>
          </DrawerHeader>

          <div
            className="flex-1 space-y-5 overflow-y-auto overscroll-contain p-4 pt-0"
            data-vaul-no-drag
          >
            {/* My Favorites Section */}
            <div>
              <h3 className="font-medium mb-3">My Favorites</h3>
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="my-favorites"
                    checked={tempFilters.myFavorites}
                    onCheckedChange={(checked) =>
                      setTempFilters(prev => ({ ...prev, myFavorites: checked as boolean }))
                    }
                    disabled={!hasFavorites}
                  />
                  <Label
                    htmlFor="my-favorites"
                    className={`text-sm ${!hasFavorites ? 'text-muted-foreground' : ''}`}
                  >
                    Only show my favorited items {!hasFavorites && '(no favorites)'}
                  </Label>
                </div>
              </div>
            </div>

            <Separator />

            {/* Featured Section */}
            <div>
              <h3 className="font-medium mb-3">Featured</h3>
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="featured"
                    checked={tempFilters.featured}
                    onCheckedChange={(checked) =>
                      handleFeaturedChange(checked as boolean)
                    }
                  />
                  <Label htmlFor="featured" className="text-sm">
                    Only show featured items
                  </Label>
                </div>
              </div>
            </div>

            <Separator />

            {/* Price Range Section */}
            <div>
              <h3 className="font-medium mb-3">Price Range</h3>
              <div className="space-y-3">
                {PRICE_RANGES.map((range) => (
                  <div key={range.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={range.id}
                      checked={tempFilters.priceRanges.includes(range.id)}
                      onCheckedChange={(checked) =>
                        handlePriceRangeChange(range.id, checked as boolean)
                      }
                    />
                    <Label htmlFor={range.id} className="text-sm">
                      {range.label}
                    </Label>
                  </div>
                ))}
              </div>
            </div>

            <Separator />

            {/* Rating Section */}
            <div>
              <h3 className="font-medium mb-3">Rating</h3>
              <div className="space-y-3">
                {RATING_RANGES.map((range) => (
                  <div key={range.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={range.id}
                      checked={tempFilters.ratingRanges.includes(range.id)}
                      onCheckedChange={(checked) =>
                        handleRatingRangeChange(range.id, checked as boolean)
                      }
                    />
                    <Label htmlFor={range.id} className="text-sm">
                      {range.label}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DrawerFooter className="border-t bg-background justify-center px-4 pt-3 pb-[1.6em]">
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={handleCancel} className="flex-1">
                Cancel
              </Button>
              <Button variant="outline" onClick={handleClearAll} className="flex-1">
                Clear All
              </Button>
              <Button onClick={handleApply} className="flex-1">
                Apply
              </Button>
            </div>
          </DrawerFooter>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
